import type { DataGridProProps, GridColDef, GridRowClassNameParams } from '@mui/x-data-grid-pro';
import { AccordionContent, AccordionItem, AccordionTrigger } from '@tempo-io/sui-accordion';
import { SystemStatusBadge } from '@tempo-io/sui-badge';
import type React from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DEPENDENCY_TABLE_LAYOUT,
  DEPENDENCY_TABLE_SX,
} from '@/features/item-panel/components/tabs/dependencies/lib/constants';
import type {
  DependencyRow,
  DependencySectionValue,
  ItemWithSchedule,
} from '@/features/item-panel/components/tabs/dependencies/lib/types';
import { toItemStubs } from '@/features/item-panel/components/tabs/dependencies/lib/utils';
import { NoDependenciesOverlay } from '@/features/item-panel/components/tabs/dependencies/NoDependenciesOverlay';
import { ItemsTable } from '@/features/items/components/table/ItemsTable';
import type { ItemsTableRow } from '@/features/items/components/table/types';

interface DependencySectionProps {
  value: DependencySectionValue;
  title: string;
  description?: string;
  blockedBadgeLabel: string | null;
  groupingColDef: DataGridProProps<ItemsTableRow>['groupingColDef'];
  columns: GridColDef<DependencyRow>[];
  rows: ItemWithSchedule[];
  isLoading?: boolean;
  rowClassName?: (params: GridRowClassNameParams<ItemsTableRow>) => string;
  addControl?: React.ReactNode;
  removeError?: string | null;
}

export const DependencySection = ({
  value,
  title,
  description,
  blockedBadgeLabel,
  groupingColDef,
  columns,
  rows,
  isLoading,
  rowClassName,
  addControl,
  removeError,
}: DependencySectionProps) => {
  const { t } = useTranslation();
  const dataset = useMemo(() => toItemStubs(rows), [rows]);
  return (
    <AccordionItem
      value={value}
      className="rounded-lg border border-border-neutral bg-bg-surface-base px-4 last:border"
    >
      <div className="flex items-center gap-2">
        <div className="flex-1">
          <AccordionTrigger className="w-full text-sm font-semibold leading-5 text-text-neutral-primary hover:no-underline [&>svg]:hidden">
            <span className="flex flex-1 items-start gap-2">
              <span className="flex-1">
                {title} ({rows.length})
                {!!description && (
                  <span className="mt-1 block text-sm font-normal leading-5 text-text-neutral-secondary">
                    {description}
                  </span>
                )}
              </span>
              {!!blockedBadgeLabel && (
                <SystemStatusBadge variant="error" className="p-2">
                  {blockedBadgeLabel}
                </SystemStatusBadge>
              )}
            </span>
          </AccordionTrigger>
        </div>
        {addControl}
        <AccordionTrigger
          aria-label={t(($) => $.adaptivePlanner.itemPanel.dependenciesSection.toggleSection)}
          className="shrink-0 py-0"
        />
      </div>
      <AccordionContent className="pt-2">
        <div className="border-t border-border-neutral max-h-[15rem] overflow-y-auto">
          <ItemsTable
            dataset={dataset}
            items={rows}
            layout={DEPENDENCY_TABLE_LAYOUT}
            customGroupingColDef={groupingColDef}
            customColumns={columns}
            checkboxSelection={false}
            disableRowReordering
            readOnly
            sortingMode="client"
            disableColumnSorting={false}
            disableColumnMenu={false}
            slotProps={{
              basePopper: { style: { pointerEvents: 'auto' } },
            }}
            loading={isLoading}
            density="comfortable"
            showColumnVerticalBorder={false}
            showCellVerticalBorder={false}
            sx={DEPENDENCY_TABLE_SX}
            autoHeight
            getRowClassName={rowClassName}
            slots={{ noRowsOverlay: NoDependenciesOverlay }}
            fetchHealth={false}
            fetchSchedules={false}
          />
        </div>
        {!!removeError && (
          <span
            role="alert"
            aria-live="polite"
            className="mt-2 block text-xs text-text-error"
            data-testid="remove-dependency-error"
          >
            {removeError}
          </span>
        )}
      </AccordionContent>
    </AccordionItem>
  );
};
